/**
 * Turning two-step verification on and off, from the account menu.
 *
 * Optional, per account. Somebody who wants it does not have to wait for the
 * owner to decide, and an owner who wants it on their own account does not
 * impose a phone on the cashier working the counter.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import QRCode from 'qrcode';
import { api } from '../lib/api';
import { Loading, Modal, TextInput, useToast } from '../components/ui';

interface TwoFactorState {
  enabled: boolean;
  pending: boolean;
  confirmedAt: string | null;
  recoveryCodesRemaining: number;
}

interface Enrolment {
  secret: string;
  otpauthUri: string;
}

/** The key in the groups of four an authenticator prints it in. */
const grouped = (secret: string): string => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

export function TwoFactorModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ['2fa'],
    queryFn: () => api.get<TwoFactorState>('/api/auth/2fa'),
  });

  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  const done = () => {
    void queryClient.invalidateQueries({ queryKey: ['2fa'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  if (codes) {
    return <RecoveryCodes codes={codes} onClose={onClose} />;
  }

  if (enrolment) {
    return (
      <Enrol
        enrolment={enrolment}
        onCancel={() => setEnrolment(null)}
        onEnabled={(issued) => {
          setEnrolment(null);
          setCodes(issued);
          done();
        }}
      />
    );
  }

  return (
    <Modal
      title="Two-step verification"
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      {status.isLoading || !status.data ? (
        <Loading />
      ) : status.data.enabled ? (
        <TurnedOn state={status.data} onChanged={done} onCodes={setCodes} />
      ) : (
        <TurnedOff onStarted={setEnrolment} />
      )}
    </Modal>
  );
}

function TurnedOff({ onStarted }: { onStarted: (enrolment: Enrolment) => void }) {
  const { fail } = useToast();
  const begin = useMutation({
    mutationFn: () => api.post<Enrolment>('/api/auth/2fa/setup', {}),
    onSuccess: onStarted,
    onError: fail,
  });

  return (
    <div className="col gap-16">
      <p>
        With this on, signing in asks for a six-digit code from your phone as well as your
        password. Somebody who learns your password still cannot get in.
      </p>
      <p className="small muted">
        You will need an authenticator app — Google Authenticator, Microsoft Authenticator and
        Authy all work. It is free and works without a signal.
      </p>
      <button
        className="btn primary"
        style={{ alignSelf: 'flex-start' }}
        disabled={begin.isPending}
        onClick={() => begin.mutate()}
      >
        {begin.isPending ? 'Setting up…' : 'Set it up'}
      </button>
    </div>
  );
}

function Enrol({
  enrolment,
  onCancel,
  onEnabled,
}: {
  enrolment: Enrolment;
  onCancel: () => void;
  onEnabled: (codes: string[]) => void;
}) {
  const { fail } = useToast();
  const [code, setCode] = useState('');
  const [showKey, setShowKey] = useState(false);

  const qr = useQuery({
    queryKey: ['2fa-qr', enrolment.otpauthUri],
    queryFn: () =>
      QRCode.toString(enrolment.otpauthUri, {
        type: 'svg',
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 168,
        color: { dark: '#0d2b2a', light: '#ffffff' },
      }),
    staleTime: Infinity,
  });

  const enable = useMutation({
    mutationFn: () => api.post<{ codes: string[] }>('/api/auth/2fa/enable', { code: code.trim() }),
    onSuccess: (result) => onEnabled(result.codes),
    onError: fail,
  });

  return (
    <Modal
      title="Scan this with your authenticator"
      onClose={onCancel}
      footer={
        <>
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={code.trim().length < 6 || enable.isPending}
            onClick={() => enable.mutate()}
          >
            {enable.isPending ? 'Checking…' : 'Turn it on'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <div className="row gap-16" style={{ alignItems: 'flex-start' }}>
          <div className="qr" style={{ width: 168, flex: 'none' }}>
            {qr.data ? (
              <span dangerouslySetInnerHTML={{ __html: qr.data }} />
            ) : (
              <div className="qr-placeholder" style={{ width: 168, height: 168 }} />
            )}
          </div>
          <div className="col gap-6">
            <p className="small">
              Open your authenticator app, choose <b>Add account</b>, and scan this.
            </p>
            <button className="btn ghost sm" onClick={() => setShowKey((open) => !open)}>
              {showKey ? 'Hide the key' : 'Cannot scan it?'}
            </button>
            {showKey ? (
              <>
                <code className="mono small" style={{ wordBreak: 'break-all' }}>
                  {grouped(enrolment.secret)}
                </code>
                <span className="small muted">
                  Type this in instead, choosing “time based” if you are asked.
                </span>
              </>
            ) : null}
          </div>
        </div>

        <TextInput
          label="Now enter the code it shows"
          autoFocus
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="123456"
          hint="This proves your phone and Kortex agree before anything is switched on."
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function TurnedOn({
  state,
  onChanged,
  onCodes,
}: {
  state: TwoFactorState;
  onChanged: () => void;
  onCodes: (codes: string[]) => void;
}) {
  const { push, fail } = useToast();
  const [password, setPassword] = useState('');
  const [turningOff, setTurningOff] = useState(false);

  const disable = useMutation({
    mutationFn: () => api.post('/api/auth/2fa/disable', { password }),
    onSuccess: () => {
      push('Two-step verification turned off');
      setTurningOff(false);
      setPassword('');
      onChanged();
    },
    onError: fail,
  });

  const fresh = useMutation({
    mutationFn: () => api.post<{ codes: string[] }>('/api/auth/2fa/recovery-codes', {}),
    onSuccess: (result) => {
      onCodes(result.codes);
      onChanged();
    },
    onError: fail,
  });

  return (
    <div className="col gap-16">
      <div className="row gap-6">
        <span className="chip ok">On</span>
        <span className="small muted">
          Since {state.confirmedAt ? new Date(state.confirmedAt).toLocaleDateString('en-IN') : '—'}
        </span>
      </div>

      <p className="small">
        Signing in asks for a code from your authenticator as well as your password.
      </p>

      <div className="col gap-6">
        <b className="small">
          {state.recoveryCodesRemaining} recovery code
          {state.recoveryCodesRemaining === 1 ? '' : 's'} left
        </b>
        <span className="small muted">
          {state.recoveryCodesRemaining <= 3
            ? 'Running low. Generate a new set and keep them somewhere safe.'
            : 'Each one gets you in once, for the day the phone is not with you.'}
        </span>
        <button
          className="btn sm"
          style={{ alignSelf: 'flex-start' }}
          disabled={fresh.isPending}
          onClick={() => fresh.mutate()}
        >
          {fresh.isPending ? 'Generating…' : 'New recovery codes'}
        </button>
      </div>

      <hr className="sep" />

      {turningOff ? (
        <div className="col gap-6">
          <TextInput
            label="Confirm your password to turn it off"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="row gap-6">
            <button className="btn" onClick={() => setTurningOff(false)}>
              Keep it on
            </button>
            <button
              className="btn danger"
              disabled={!password || disable.isPending}
              onClick={() => disable.mutate()}
            >
              {disable.isPending ? 'Turning off…' : 'Turn it off'}
            </button>
          </div>
        </div>
      ) : (
        <button
          className="btn danger"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setTurningOff(true)}
        >
          Turn off two-step verification
        </button>
      )}
    </div>
  );
}

/**
 * The recovery codes, shown once.
 *
 * Only their hashes are kept, so this really is the only time they can be read.
 * The wording says so, because the alternative is somebody closing the box and
 * finding out in six months when their phone is gone.
 */
function RecoveryCodes({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const { push } = useToast();
  const text = codes.join('\n');

  return (
    <Modal
      title="Save your recovery codes"
      onClose={onClose}
      footer={
        <>
          <button
            className="btn"
            onClick={() => {
              void navigator.clipboard.writeText(text);
              push('Copied');
            }}
          >
            Copy
          </button>
          <button className="btn" onClick={() => window.print()}>
            Print
          </button>
          <button className="btn primary" onClick={onClose}>
            I have saved them
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <p>
          Each of these gets you in once if you cannot reach your authenticator. Keep them
          somewhere only you can get to — they are as good as your password.
        </p>
        <div className="doc" style={{ padding: 12 }}>
          <div className="recovery-codes mono">
            {codes.map((code) => (
              <span key={code}>{code}</span>
            ))}
          </div>
        </div>
        <p className="small muted">
          This is the only time they are shown. If you lose them, generate a new set from this
          screen — the old ones stop working the moment you do.
        </p>
      </div>
    </Modal>
  );
}
